import type {
  CarryForwardOutput,
  CoContributionOutput,
  SacrificeOutput,
  SpouseOffsetOutput,
} from "./types.js";

// Plain-English result copy for the super-boost family. Centralised in the rules
// package so every surface (Expo result screens, "my results", PDF reports)
// renders identical wording from one source — which matters for regulation, not
// just DRY. Every string here is INFORMATION, not advice (validation §3): it
// states what the entitlement/concession IS and how it derives from the ATO
// rules, with NO "save $X", NO "you should contribute", NO product
// recommendation, and — binding here — NO fund / super-product handoff (super is
// s764A). Conditions are stated before any number; the carry-forward expiry is a
// CALM DATED FACT, never a ticking-urgency / "don't miss out" nudge. Drift from
// information → advice is the kill-threat, so the copy lives beside the rules.

const DISCLAIMER = "Educational tool. Not financial advice.";

/** Whole-dollar AUD with thousands separators. Local to the copy layer so the
 * wording renders the same numbers everywhere, independent of any app's UI. */
export function formatAud(amount: number): string {
  return `A$${Math.round(amount).toLocaleString("en-AU")}`;
}

// ---------------------------------------------------------------------------
// coContribution — conditions before the number; mechanism, not recommendation.
// ---------------------------------------------------------------------------

export function coContributionHeadline(out: CoContributionOutput): string {
  if (!out.eligible) {
    return "Based on the details entered, one or more co-contribution conditions are not met";
  }
  return out.coContribution === 0
    ? "At this income, the government co-contribution tapers to A$0"
    : `If these conditions are met, the ATO would add up to ${formatAud(out.coContribution)} to your super`;
}

export function coContributionBullet(out: CoContributionOutput): string {
  return `The co-contribution is 50 cents for every A$1 of eligible after-tax (non-concessional) contribution, up to a maximum that applies at lower incomes and reduces as total income rises. On a total income of ${formatAud(
    out.totalIncome,
  )}, the income test allows up to ${formatAud(out.incomeTestMax)}.`;
}

/** Lists which conditions are not met — descriptive, not a nudge to fix them. */
export function coContributionConditionsBullet(
  out: CoContributionOutput,
): string | null {
  if (out.eligible) return null;
  return `Eligibility for the co-contribution depends on conditions including making an eligible after-tax contribution, the 10% work/income test, being under 71 at year end, a total super balance below the general transfer balance cap, lodging a tax return, and visa status. From the details entered, these are not all met: ${out.failedConditions.join(
    ", ",
  )}.`;
}

// ---------------------------------------------------------------------------
// spouseOffset
// ---------------------------------------------------------------------------

export function spouseOffsetHeadline(out: SpouseOffsetOutput): string {
  return out.offset === 0
    ? "At your spouse's income, the spouse-contribution offset is A$0"
    : `A spouse contribution could attract a tax offset of up to ${formatAud(out.offset)}`;
}

export function spouseOffsetBullet(out: SpouseOffsetOutput): string {
  return `The spouse-contribution tax offset is 18% of the lesser of A$3,000 and the contribution you make, with the A$3,000 reducing by A$1 for every A$1 your spouse's income is over A$37,000 (nil at A$40,000). On a spouse income of ${formatAud(
    out.spouseIncome,
  )} and a contribution of ${formatAud(
    out.contribution,
  )}, the eligible base is ${formatAud(out.eligibleBase)}.`;
}

// ---------------------------------------------------------------------------
// carryForwardHeadroom — CALM dated fact on expiry, no urgency framing.
// ---------------------------------------------------------------------------

export function carryForwardHeadline(out: CarryForwardOutput): string {
  return out.availableHeadroom === 0
    ? "You have no unused concessional cap to carry forward in this window"
    : `You have ${formatAud(out.availableHeadroom)} of unused concessional cap from the past five years`;
}

export function carryForwardBullet(out: CarryForwardOutput): string {
  const base = `Unused concessional cap from the previous five financial years can be carried forward and used in a later year. From the years entered, your unused amount totals ${formatAud(
    out.availableHeadroom,
  )}.`;
  if (!out.canUse) {
    return `${base} Note: carry-forward amounts can only be used in a year when your total super balance was below A$500,000 at the previous 30 June — from the balance entered, that condition is not met this year.`;
  }
  return base;
}

/** A calm, dated statement of when the oldest slice expires — never "act now". */
export function carryForwardExpiryBullet(out: CarryForwardOutput): string | null {
  if (out.oldestExpiring === null) return null;
  return `Your oldest unused amount is ${formatAud(
    out.oldestExpiring.unused,
  )} from ${out.oldestExpiring.fy}. Under the five-year rule this amount is available to use until the end of ${out.oldestExpiring.expiresEndOfFy} (30 June); after that, the carry-forward rule expires it.`;
}

/** Illustrative tax-differential mechanics — framed as the rule, not a saving. */
export function carryForwardTaxBullet(out: CarryForwardOutput): string | null {
  if (!out.canUse || out.taxSavingIfContributed === 0) return null;
  return `Concessional contributions are taxed in your super fund at 15%. Where your marginal income-tax rate is higher than 15%, contributing concessionally is taxed at the lower 15% rate instead — on ${formatAud(
    out.availableHeadroom,
  )} that difference works out to about ${formatAud(
    out.taxSavingIfContributed,
  )} in this year's rates.`;
}

// ---------------------------------------------------------------------------
// sacrificeTaxSaving — low-income honesty.
// ---------------------------------------------------------------------------

export function sacrificeHeadline(out: SacrificeOutput): string {
  if (out.concessionInverts) {
    return "At this income, contributing concessionally is taxed at 15% with no income-tax saving to offset it";
  }
  return out.taxSaving === 0
    ? "At this contribution, the concession works out to A$0"
    : `Contributing concessionally is taxed at 15% instead of your marginal rate, a difference of ${formatAud(out.taxSaving)}`;
}

export function sacrificeBullet(out: SacrificeOutput): string {
  if (out.concessionInverts) {
    return `Concessional contributions are taxed at 15% in your super fund. Your marginal income-tax rate is ${pct(
      out.marginalRate,
    )}, so at this income the 15% in-fund tax is not offset by a higher income-tax rate. The low income super tax offset (LISTO) can refund the 15% on concessional contributions for low earners.`;
  }
  return `Concessional contributions are taxed at 15% in your super fund instead of your marginal income-tax rate of ${pct(
    out.marginalRate,
  )}. The difference is ${pct(
    out.concessionPerDollar,
  )} per A$1 — a small but real concession even at lower incomes. On ${formatAud(
    out.contribution,
  )} that is ${formatAud(out.taxSaving)}.`;
}

export function sacrificeListoBullet(out: SacrificeOutput): string | null {
  if (out.listo === null || out.listo === 0) return null;
  return `The low income super tax offset (LISTO) would add ${formatAud(
    out.listo,
  )}, refunding the 15% tax on your concessional contributions at this income (up to A$500).`;
}

// ---------------------------------------------------------------------------
// shared
// ---------------------------------------------------------------------------

function pct(fraction: number): string {
  return `${+(fraction * 100).toFixed(2)}%`;
}

/** Source-attribution + estimate framing. The ATO finalises at lodgement. */
export function sourceBullet(fy: string, verifiedAt: string): string {
  return `These figures are an estimate using the ATO super rates and thresholds for ${fy} (last verified ${verifiedAt}). The ATO works out the final amounts when you lodge — always check ato.gov.au. ${DISCLAIMER}`;
}
